import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { fmtTL, fmtDate, fmtNum } from "@/lib/utils";
import { PriceBadge } from "@/components/HakedisLinesEditor";

export default function PaymentDetailDialog({ payment, onClose }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!payment) return;
    setLoading(true);
    api.get(`/progress-payments/${payment.id}/lines`).then(r => setLines(r.data)).finally(() => setLoading(false));
  }, [payment]);

  const active = lines.filter(l => l.qty_this || l.amount_this);
  const [onlyActive, setOnlyActive] = useState(true);
  const shown = onlyActive ? active : lines;
  const high = active.filter(l => l.ref?.high).length;

  return (
    <Dialog open={!!payment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="payment-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {payment?.firm_name || "Hakediş"} — Hakediş #{payment?.payment_no}
          </DialogTitle>
        </DialogHeader>
        {payment && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
            <Info label="Dönem" value={`${fmtDate(payment.period_start)} — ${fmtDate(payment.period_end)}`} />
            <Info label="Bu Hakediş" value={fmtTL(payment.gross_amount)} accent />
            <Info label="Kümülatif" value={fmtTL(payment.cumulative_amount)} />
            <Info label="Kalem (bu dönem / toplam)" value={`${active.length} / ${lines.length}`} />
          </div>
        )}
        {payment?.deduction_items?.length > 0 && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm" data-testid="detail-deductions">
            <div className="font-semibold mb-1 text-rose-700 dark:text-rose-400">Kesintiler: -{fmtTL(payment.deductions)} · Net: {fmtTL(payment.net_amount)}</div>
            {payment.deduction_items.map((d, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5"><span>{d.category} — {d.name}{d.period ? ` (${fmtDate(d.period)})` : ""}</span><span className="font-mono-nums">{fmtTL(d.amount)}</span></div>
            ))}
          </div>
        )}
        {loading ? <div className="p-8 text-center text-muted-foreground">Yükleniyor...</div> : (
          <>
          <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
            <Switch checked={onlyActive} onCheckedChange={setOnlyActive} data-testid="only-active-toggle" />
            Sadece bu dönem üretim yapılan kalemler
          </label>
          {high > 0 && <div className="text-sm font-semibold text-rose-600 dark:text-rose-400" data-testid="detail-high-count">{high} kalemde birim fiyat, önceki projelerdeki ortalamanın üzerinde</div>}
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Proje</TableHead><TableHead>Poz No</TableHead><TableHead>Tanım</TableHead><TableHead>Birim</TableHead>
                  <TableHead className="text-right">Birim Fiyat</TableHead><TableHead className="text-right">Bu Dönem Miktar</TableHead>
                  <TableHead className="text-right">Toplam Miktar</TableHead><TableHead className="text-right">Bu Dönem Tutar</TableHead>
                  <TableHead className="text-right">Toplam Tutar</TableHead>
                  <TableHead>Fiyat Kontrolü</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map(l => (
                  <TableRow key={l.id} className={l.amount_this ? "" : "opacity-50"}>
                    <TableCell className="text-xs">{l.project_name}</TableCell>
                    <TableCell className="font-mono-nums text-xs">{l.poz_no}</TableCell>
                    <TableCell className="text-sm max-w-xs">{l.description}</TableCell>
                    <TableCell>{l.unit}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtTL(l.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtNum(l.qty_this)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtNum(l.qty_total)}</TableCell>
                    <TableCell className="text-right font-mono-nums font-semibold text-amber-700 dark:text-amber-400">{fmtTL(l.amount_this)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtTL(l.amount_total)}</TableCell>
                    <TableCell>{l.amount_this ? <PriceBadge info={l.ref} testid={`detail-price-${l.id}`} /> : null}</TableCell>
                  </TableRow>
                ))}
                {shown.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">Bu hakedişe ait kalem bulunmuyor</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-muted/30"}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono-nums font-bold mt-0.5">{value}</div>
    </div>
  );
}
